import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isClientDead, subscribeClientHealth, installClientErrorWatch } from '../lib/clientHealth';
import { isMarketingPath } from '../lib/marketingPaths';

/**
 * StaleConnectionBanner — "this window stopped hearing from the server."
 *
 * Walter 2026-09-06. His PWA window's Firestore client died at 20:05:55 on
 * 2026-09-05 and he found out 22 hours later, by opening a second window and
 * noticing it had content the first one didn't. Nothing in the app had told
 * him: the header said "synced" the whole time, because his edits genuinely
 * were being saved — over the /api/saveList fallback — while nothing was
 * arriving in the other direction. See lib/clientHealth for the mechanism.
 *
 * The remedy is a reload, and only a reload: the SDK's client cannot be
 * revived once its internal queue has panicked. So this banner has one
 * button, and it does that.
 *
 * Distinct from ExtensionBlockHint, which is about a blocker stopping edits
 * from LEAVING. This one is the opposite direction and the more dangerous
 * one, because the failure is invisible by construction — a window that
 * hears nothing looks exactly like a window with nothing to hear.
 *
 * Dismissal is session-only, deliberately. The two-tier "remind me tomorrow /
 * don't remind me" that ExtensionBlockHint uses is right for an advisory
 * about someone's browser setup; it would be wrong here, where the window in
 * front of the user is actively showing stale content and a reload fixes it
 * in two seconds. A × hides it until the next reload, which is the same thing
 * as fixing it.
 */

export default function StaleConnectionBanner() {
  const [dead, setDead] = useState(() => isClientDead());
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // The assertion usually fires on the SDK's own timer rather than inside a
    // call we made, so catching it needs window-level handlers. Installed
    // here because this component mounts once, app-wide, for the session.
    installClientErrorWatch();
    return subscribeClientHealth(setDead);
  }, []);

  // Marketing routes don't touch Firestore — same rationale as the other
  // hints. Nothing to be stale about.
  if (!dead || dismissed || isMarketingPath(location.pathname)) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1900,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        padding: '10px',
      }}
    >
      <div
        style={{
          maxWidth: 640,
          width: '100%',
          background: 'rgba(232, 98, 44, 0.96)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 'var(--radius)',
          padding: '10px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          lineHeight: 1.45,
          pointerEvents: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1.5 }}>⚠</span>
          {/* Walter 2026-09-06, cutting the first draft down by two thirds:
              "People don't like to read." He's right — the original explained
              the mechanism (edits still save, incoming changes don't arrive)
              to someone who only needs to know the state and the remedy, both
              of which fit in six words. The button is right there; the
              sentence only has to get them to it. His "may be stale" became
              "may be out of date" — same length, no vocabulary to know. */}
          <span style={{ flex: 1 }}>
            This window may be <strong style={{ fontWeight: 600 }}>out of date</strong>. Reload to update.
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            title="hide this until the next reload"
            style={{
              background: 'transparent',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 4,
              padding: '4px 8px',
              cursor: 'pointer',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              whiteSpace: 'nowrap',
              opacity: 0.75,
            }}
          >
            not now
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            title="reload this window so it starts receiving changes again"
            style={{
              background: '#fff',
              color: 'rgb(232, 98, 44)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            reload
          </button>
        </div>
      </div>
    </div>
  );
}
